import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiArrowLeft, FiEdit2, FiBookOpen, FiPlus,
    FiLayers, FiUser, FiCheckCircle, FiClock, FiTrendingUp
} from "react-icons/fi";
import subjectService from "../../services/subjectService";
import courseService from "../../services/courseService";
import syllabusService from "../../services/syllabusService";
import { useApp } from "../../context/AppContext";

const TYPE_STYLES = {
    Theory: { bg: "#e8f1f8", color: "#002747" },
    Practical: { bg: "#dcfce7", color: "#15803d" },
    Elective: { bg: "#fef9c3", color: "#92400e" },
    Project: { bg: "#fce7f3", color: "#9d174d" },
    Other: { bg: "#f3f4f6", color: "#6b7280" },
};

const COURSE_COLORS = {
    "D. PHARM": "#003b67",
    "B. PHARM": "#059669",
    "M. PHARM": "#7c3aed",
    "D. Pharm": "#003b67",
    "B. Pharm": "#059669",
    "M. Pharm": "#7c3aed",
};

function InfoRow({ label, value }) {
    if (!value && value !== 0) return null;
    return (
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid var(--border)" }}>
            <span style={{ color: "var(--text-muted)" }}>{label}</span>
            <span style={{ fontWeight: 600 }}>{value}</span>
        </div>
    );
}

export default function SubjectDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [subject, setSubject] = useState(null);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});
    const { academicYear } = useApp();
    const [progressData, setProgressData] = useState(null);

    useEffect(() => {
        courseService
            .getAll({ isActive: true })
            .then((res) => setCourses(res.data || []))
            .catch(() => {});
    }, []);

    const getCourseName = useCallback((courseVal) => {
        if (!courseVal) return "—";
        if (typeof courseVal === "object") {
            return courseVal.shortName || courseVal.courseName || "—";
        }
        const match = courses.find((c) => c._id === courseVal || c.shortName === courseVal || c.courseName === courseVal);
        if (match) return match.shortName || match.courseName;
        return courseVal || "—";
    }, [courses]);

    useEffect(() => {
        subjectService.getById(id)
            .then((res) => {
                setSubject(res.data);
                if (res.data.syllabus?.length > 0) {
                    setExpanded({ [res.data.syllabus[0].unitNo]: true });
                }
            })
            .catch((err) => { toast.error(err.message); navigate("/subjects"); })
            .finally(() => setLoading(false));

        syllabusService.getSubjectProgress(id, { academicYear: academicYear?._id })
            .then((res) => setProgressData(res.data?.data || null))
            .catch(() => {});
    }, [id, navigate, academicYear]);


    if (loading) return <div className="loading-center"><div className="spinner" />Loading subject…</div>;
    if (!subject) return null;

    const ts = TYPE_STYLES[subject.type] || TYPE_STYLES.Other;
    const courseLabel = getCourseName(subject.course);
    const color = COURSE_COLORS[courseLabel] || COURSE_COLORS[courseLabel?.toUpperCase()] || "#6b7280";
    const totalHours = subject.syllabus?.reduce((s, u) => s + (u.hours || 0), 0) || 0;
    const totalTopics = subject.syllabus?.reduce((s, u) => s + (u.topics?.filter(Boolean).length || 0), 0) || 0;
    const teachers = subject.teachers || [];

    const toggleUnit = (unitNo) =>
        setExpanded((p) => ({ ...p, [unitNo]: !p[unitNo] }));

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
                    <div style={{ width: 48, height: 48, borderRadius: 14, background: ts.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <FiBookOpen size={22} color={ts.color} />
                    </div>
                    <div>
                        <h1 className="page-title" style={{ marginBottom: 4 }}>{subject.name}</h1>
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ background: `${color}18`, color, padding: "2px 9px", borderRadius: 99, fontSize: 12, fontWeight: 700 }}>
                                {courseLabel}
                            </span>
                            <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                                {subject.yearNumber ? `${subject.yearNumber}${subject.yearNumber === 1 ? "st" : subject.yearNumber === 2 ? "nd" : subject.yearNumber === 3 ? "rd" : "th"} Year` : "—"}
                            </span>
                            {subject.semesterNumber && (
                                <span style={{ fontSize: 12, background: "#f1f5f9", color: "#475569", padding: "2px 9px", borderRadius: 99, fontWeight: 600 }}>
                                    Semester {subject.semesterNumber}
                                </span>
                            )}
                            <span style={{ ...ts, padding: "2px 9px", borderRadius: 99, fontSize: 11.5, fontWeight: 600 }}>
                                {subject.type}
                            </span>
                            {subject.code && <span className="badge badge-blue">{subject.code}</span>}
                            {!subject.isActive && (
                                <span style={{ fontSize: 11, background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: 99, fontWeight: 600 }}>Inactive</span>
                            )}
                        </div>
                    </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/subjects")}>
                        <FiArrowLeft size={14} /> Back
                    </button>
                    <button className="btn btn-outline" onClick={() => navigate(`/subjects/${id}/edit`)}>
                        <FiEdit2 size={14} /> Edit
                    </button>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 260px", gap: "1rem", alignItems: "start" }}>

                {/* ── Left: Syllabus ── */}
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                            <FiLayers size={13} /> Syllabus
                        </span>
                        {subject.syllabus?.length > 0 && (
                            <div style={{ display: "flex", gap: "0.5rem" }}>
                                <button className="btn btn-outline btn-sm" onClick={() => {
                                    const all = {};
                                    subject.syllabus.forEach((u) => { all[u.unitNo] = true; });
                                    setExpanded(all);
                                }}>Expand all</button>
                                <button className="btn btn-outline btn-sm" onClick={() => setExpanded({})}>Collapse all</button>
                            </div>
                        )}
                    </div>

                    {!subject.syllabus?.length ? (
                        <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>
                            <FiLayers size={32} style={{ opacity: 0.2, display: "block", margin: "0 auto 0.75rem" }} />
                            <p style={{ fontSize: 13 }}>No syllabus added yet.</p>
                            <button className="btn btn-outline btn-sm" style={{ marginTop: "0.5rem" }} onClick={() => navigate(`/subjects/${id}/edit`)}>
                                <FiEdit2 size={12} /> Add Syllabus
                            </button>
                        </div>
                    ) : (
                        <div style={{ padding: "0.75rem 1.25rem" }}>
                            {subject.syllabus.map((unit) => {
                                const isOpen = expanded[unit.unitNo];
                                return (
                                    <div key={unit.unitNo} style={{ border: "1px solid var(--border)", borderRadius: 8, marginBottom: "0.5rem", overflow: "hidden" }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center", gap: "0.65rem", padding: "0.65rem 0.9rem", background: isOpen ? "#e8f1f8" : "#f8fafc", cursor: "pointer", userSelect: "none", borderBottom: isOpen ? "1px solid var(--border)" : "none" }}
                                            onClick={() => toggleUnit(unit.unitNo)}
                                        >
                                            <div style={{ width: 26, height: 26, borderRadius: 7, background: color + "18", color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                                                {unit.unitNo}
                                            </div>
                                            <span style={{ flex: 1, fontWeight: 700, fontSize: 13, color: isOpen ? color : "var(--text)" }}>
                                                {unit.title}
                                            </span>
                                            <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", fontSize: 12, color: "var(--text-muted)" }}>
                                                {unit.topics?.filter(Boolean).length > 0 && (
                                                    <span>{unit.topics.filter(Boolean).length} topic{unit.topics.filter(Boolean).length !== 1 ? "s" : ""}</span>
                                                )}
                                                {unit.hours > 0 && <span>{unit.hours}h</span>}
                                                <span style={{ fontSize: 11, color: isOpen ? color : "var(--text-muted)" }}>{isOpen ? "▲" : "▼"}</span>
                                            </div>
                                        </div>
                                        {isOpen && unit.topics?.filter(Boolean).length > 0 && (
                                            <div style={{ padding: "0.75rem 1rem" }}>
                                                <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                                                    {unit.topics.filter(Boolean).map((topic, ti) => (
                                                        <li key={ti} style={{ fontSize: 13, color: "var(--text)", padding: "3px 0", lineHeight: 1.5 }}>
                                                            <div
                                                                className="rich-topic-display"
                                                                dangerouslySetInnerHTML={{ __html: topic }}
                                                            />
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {isOpen && !unit.topics?.filter(Boolean).length && (
                                            <div style={{ padding: "0.65rem 1rem", fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>No topics listed.</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* ── Right: Info panel ── */}
                <div style={{ display: "grid", gap: "1rem" }}>

                    {/* Subject info */}
                    <div className="card">
                        <div className="card-header" style={{ fontSize: 13 }}>Subject Info</div>
                        <div style={{ padding: "0.85rem 1.1rem" }}>
                            <InfoRow label="Course" value={courseLabel} />
                            <InfoRow label="Year" value={subject.yearNumber ? `${subject.yearNumber}${subject.yearNumber === 1 ? "st" : subject.yearNumber === 2 ? "nd" : subject.yearNumber === 3 ? "rd" : "th"} Year` : "—"} />
                            <InfoRow label="Type" value={subject.type} />
                            <InfoRow label="Code" value={subject.code || "—"} />
                            <InfoRow label="Credit Hours" value={subject.creditHours > 0 ? `${subject.creditHours} hrs` : "—"} />
                            <InfoRow label="Weekly Hours" value={subject.weeklyHours > 0 ? `${subject.weeklyHours} hrs/week` : "—"} />
                            {subject.description && (
                                <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 8, lineHeight: 1.5 }}>
                                    {subject.description}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Assigned teachers */}
                    <div className="card">
                        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: 13 }}>
                                <FiUser size={13} color="#7c3aed" /> Assigned Teachers
                                {teachers.length > 0 && (
                                    <span style={{ fontSize: 11, background: "#7c3aed", color: "#fff", padding: "1px 6px", borderRadius: 99, fontWeight: 700 }}>
                                        {teachers.length}
                                    </span>
                                )}
                            </span>
                            <button className="btn btn-outline btn-sm" onClick={() => navigate(`/subjects/${id}/edit`)}>
                                <FiEdit2 size={11} /> Manage
                            </button>
                        </div>

                        {teachers.length === 0 ? (
                            <div style={{ padding: "0.85rem 1.1rem" }}>
                                <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "0.6rem" }}>
                                    No teachers assigned yet.
                                </div>
                                <button className="btn btn-outline btn-sm" onClick={() => navigate(`/subjects/${id}/edit`)}
                                    style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                                    <FiPlus size={12} /> Assign Teacher
                                </button>
                            </div>
                        ) : (
                            <div>
                                {teachers.map((teacher) => {
                                    const t = teacher._id ? teacher : { _id: teacher, fullName: "—", employeeCode: "—", designation: "—" };
                                    return (
                                        <div key={String(t._id)} style={{ display: "flex", alignItems: "center", gap: "0.65rem", padding: "0.6rem 1.1rem", borderBottom: "1px solid var(--border)" }}>
                                            <div style={{ width: 32, height: 32, borderRadius: 9, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "#7c3aed", flexShrink: 0 }}>
                                                {t.fullName?.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "T"}
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 13, fontWeight: 600 }}>{t.fullName}</div>
                                                <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                    {t.employeeCode}{t.designation ? ` · ${t.designation}` : ""}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Syllabus stats */}
                    {subject.syllabus?.length > 0 && (
                        <div className="card">
                            <div className="card-header" style={{ fontSize: 13 }}>Syllabus Stats</div>
                            <div style={{ padding: "0.85rem 1.1rem" }}>
                                <InfoRow label="Units" value={subject.syllabus.length} />
                                <InfoRow label="Total Topics" value={totalTopics} />
                                <InfoRow label="Fixed Hours" value={totalHours > 0 ? `${totalHours}h` : "—"} />
                            </div>
                        </div>
                    )}

                    {/* Live Delivery Progress */}
                    {progressData && (
                        <div className="card" style={{ borderLeft: "4px solid #003b67" }}>
                            <div className="card-header" style={{ fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                                    <FiTrendingUp color="#003b67" size={13} /> Delivery Progress
                                </span>
                                <span style={{ fontSize: 11, background: "#e8f1f8", color: "#002747", padding: "1px 6px", borderRadius: 99, fontWeight: 700 }}>
                                    {progressData.completionPercentage || 0}%
                                </span>
                            </div>
                            <div style={{ padding: "0.85rem 1.1rem" }}>
                                <div style={{ marginBottom: 8 }}>
                                    <div style={{ width: "100%", height: 6, background: "#e2e8f0", borderRadius: 99, overflow: "hidden" }}>
                                        <div style={{ width: `${progressData.completionPercentage || 0}%`, height: "100%", background: progressData.completionPercentage === 100 ? "#16a34a" : "#003b67", borderRadius: 99 }} />
                                    </div>
                                </div>
                                <InfoRow label="Units Completed" value={`${progressData.completedUnitsCount || 0} / ${progressData.totalUnitsCount || 0}`} />
                                <InfoRow label="Actual Hours Taken" value={`${progressData.totalActualHours || 0}h`} />
                                <InfoRow label="Pacing Status" value={progressData.status || "Not Started"} />
                                <button
                                    className="btn btn-outline btn-sm"
                                    style={{ width: "100%", justifyContent: "center", marginTop: 8, fontSize: 12 }}
                                    onClick={() => navigate(`/subjects/performance?course=${encodeURIComponent(subject.course?._id || subject.course || "")}`)}
                                >
                                    Faculty Performance Hub
                                </button>
                            </div>
                        </div>
                    )}


                    <button
                        className="btn btn-outline"
                        style={{ width: "100%", justifyContent: "center" }}
                        onClick={() => navigate(`/subjects/new?course=${encodeURIComponent(subject.course?._id || subject.course || "")}&yearNumber=${encodeURIComponent(subject.yearNumber || "")}&semesterNumber=${encodeURIComponent(subject.semesterNumber || "")}`)}
                    >
                        <FiPlus size={13} /> Add Another Subject
                    </button>
                </div>
            </div>
        </div>
    );
}
